import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types'
import useWebSocket, {ReadyState} from 'react-use-websocket';

import * as Requests from '../../models/requests';
import * as Constants from '../../utils/consts';
import {GameMode} from '../../models/Enums';
import BlackCardPlayer from '../../components/BlackCardPlayer/BlackCardPlayer';
import WhiteCardPlayer from '../../components/WhiteCardPlayer/WhiteCardPlayer';

let wsBaseUrl = ""

if (process.env.REACT_APP_NODE_ENV === "production") {
  wsBaseUrl = `wss://${ process.env.REACT_APP_BACKEND_URL }/ws`
} else {
  wsBaseUrl = `ws://${ process.env.REACT_APP_BACKEND_URL }/ws`
}



function Board({
  gameCode,
  playerInfo,
  onReadyStateChanged,
  onRandomWon,
  onGameEnded,
}) {
  const [mode, setMode] = useState(GameMode.ANSWERER)
  const ended = useRef(false)
  // Answer Mode
  const [whiteCards, setWhiteCards] = useState([])
  const [hasBlank, setHasBlank] = useState(false)
  const [answerError, setAnswerError] = useState(null)
  const [winner, setWinner] = useState(false)
  const [roundResult, setRoundResult] = useState(null)
  const [points, setPoints] = useState(playerInfo.score)
  // Question Mode
  const [questionCard, setQuestionCard] = useState('')
  const [requiredAnswers, setRequiredAnswers] = useState(1)
  const [requiredExtraAnswers, setRequiredExtraAnswers] = useState(0)
  const [answers, setAnswers] = useState({})
  const [answeredPlayers, setAnsweredPlayers] = useState([])
  const [totalNumberOfAnswers, setTotalNumberOfAnswers] = useState(1)

  const resetRound = () => {
    setQuestionCard('')
    setRequiredAnswers(0)
    setAnswers({})
    setAnsweredPlayers([])
    setTotalNumberOfAnswers(0)
    setWinner(false)
    setRoundResult(null)
    setRequiredExtraAnswers(0)
    setAnswerError(null)
  }

  const {
    sendMessage,
    lastMessage,
    readyState,
  } = useWebSocket(`${wsBaseUrl}/${ gameCode }`, {
    onOpen: () => console.log('opened'),
    // Reconnects on every close except a refused seat (1008) and a game that ended
    shouldReconnect: (closeEvent) => closeEvent.code !== 1008 && !ended.current,
  });
  
  useEffect(() => {
    console.log("readyState: ", ReadyState[readyState])
    onReadyStateChanged(readyState);
  }, [readyState, onReadyStateChanged])

  // The seat key goes in the first message of every connection, reconnects too, never in the URL
  useEffect(() => {
    if (readyState === ReadyState.OPEN) {
      sendMessage(Requests.StartGameRequest(playerInfo.key))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyState])


  useEffect(() => {
    console.log(readyState);
  },[readyState])

  useEffect(() => {
    if (lastMessage) {
      try {
        const socketMessageData = JSON.parse(lastMessage.data);
        console.debug("lastMessage: ", socketMessageData.type)
        switch (socketMessageData.type) {
          case Constants.S_START_GAME:
            console.log('connected to game')
            break
          case Constants.S_DRAW_CARDS:
            console.log(`Cards Drawn - ${socketMessageData.cards}`)
            setWhiteCards(socketMessageData.cards)
            setHasBlank(Boolean(socketMessageData.blank))
            break
          case Constants.S_GET_QUESTION:
            console.log(socketMessageData)
            if (questionCard !== socketMessageData.question) {
              setQuestionCard(socketMessageData.question)
            }
            setRequiredAnswers(socketMessageData.required_answers)
            if (socketMessageData.current_player === playerInfo.name) {
              setMode(GameMode.ASKER)
            } else {
              setMode(GameMode.ANSWERER) // the turn can move while this player was offline
              setRequiredExtraAnswers(socketMessageData.required_extra_cards)
            }
            break
          case Constants.S_GET_ANSWERS:
            setAnswers(socketMessageData.answers)
            break
          case Constants.S_WINNER:
            setWinner(true)
            setPoints(prev => prev + 1)
            break
          case Constants.S_ROUND_RESULT:
            setRoundResult({ name: socketMessageData.name, cards: socketMessageData.cards })
            break
          case Constants.S_NEXT_PLAYER:
            resetRound()
            break
          case Constants.S_STOP_GAME:
            onRandomWon()
            break
          case Constants.S_GAME_ENDED:
            if (socketMessageData.reason === 'host_gone') {
              ended.current = true
              onGameEnded()
            }
            break
          case Constants.S_SEND_ANSWER:
            if (socketMessageData.error) { // our answer was refused; the hand is unchanged
              setAnswerError(socketMessageData.error)
              break
            }
            setAnsweredPlayers(socketMessageData.players)
            setTotalNumberOfAnswers(socketMessageData.total_players)
            break
          default:
            break
        }
      } catch (error) {
        console.error(error)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastMessage, setAnswers, setWinner, setPoints, setQuestionCard, setWhiteCards, onRandomWon, onGameEnded, playerInfo.name, questionCard])

  const handleOnSendAnswer = cards => {
    setAnswerError(null)
    sendMessage(Requests.SendAnswerRequest(cards))
  }

  const handleModeChanged = mode => {
    setMode(mode)
  }

  const handleOnGetAnswers = () => {
    sendMessage(Requests.GetAnswersRequest())
  }

  const handleOnNextPlayer = () => {
    resetRound() // the server sends NEXT_PLAYER to everyone but the judge who triggered it
    sendMessage(Requests.NextPlayerRequest())
  }

  const handleOnPickWinner = player => {
    sendMessage(Requests.PickWinnerRequest(player))
  }

  const renderGameMode = mode => (
    mode === GameMode.ASKER
        ? <BlackCardPlayer
          onModeChange={ handleModeChanged }
          answers={ answers }
          questionCard={ questionCard }
          onGetAnswersClicked={ handleOnGetAnswers }
          onNextPlayerClicked={ handleOnNextPlayer }
          onPickWinnerClicked={ handleOnPickWinner }
          requiredAnswers={ requiredAnswers }
          answeredPlayers={ answeredPlayers }
          totalNumberOfAnswers={ totalNumberOfAnswers }/>
        : <WhiteCardPlayer
          questionCard={questionCard}
          requiredAnswers={ requiredAnswers }
          sendMessage={ sendMessage }
          whiteCards={ whiteCards }
          hasBlank={ hasBlank }
          answerError={ answerError }
          onSendAnswer={ handleOnSendAnswer }
          points={ points }
          roundResult={ roundResult }
          winner={ winner }/>
  )

  return renderGameMode(mode);
}

Board.propTypes = {
  sendMessage: PropTypes.func,
  playerInfo: PropTypes.shape({
    key: PropTypes.string,
    name: PropTypes.string,
    score: PropTypes.number
  }),
  gameCode: PropTypes.string.isRequired,
}

Board.defaultProps = {
  sendMessage: () => {},
  onRandomWon: () => {},
  onGameEnded: () => {},
}

export default Board
